@if (!searchHeader) {
    <h1 class="cdk-visually-hidden">
        {{ 'research.profiles.search.title.' + selectedEntity | translate }}
    </h1>
}

<mulo-prologue
    [class.margin-bottom-sm]="advSearchRoot.isOpen || isCollection"
    [color]="searchHeaderPalette"
    class="position-relative"
>
    @if (isCollection) {
        <mulo-centered-block-layout class="collections-header" width="full">
            <div class="collections-header-content">
                <a
                    (click)="goToCollections()"
                    (muloMultipleEvents)="goToCollections()"
                    [disabled]="_window?.history?.length < 2"
                    class="has-icon back-button"
                    mat-button
                    slot="nav"
                >
                    <mat-icon class="mat-icon-rtl-mirror" svgIcon="chevron-left"></mat-icon>
                    {{ 'research.general.back' | translate }}
                </a>
                <div class="collection-details">
                    <div class="breadcrumbs">
                        <a [queryParams]="{ institution }" [routerLink]="['/collections']" class="crumb">
                            <mat-icon muloSvgViewbox svgIcon="bookmark-star"></mat-icon>
                            {{ 'research.search.collections' | translate }}
                        </a>
                        <span class="separator"></span>
                    </div>
                    <mulo-expandable
                        [ratio]="media.is('gt-sm') && headerImage ? 12 : 8"
                        buttonPosition="right"
                        collapseLabel="{{ 'research.results.description.collapse' | translate }}"
                        expandLabel="...{{ 'research.results.description.expand' | translate }}"
                    >
                        @if (headerImage) {
                            <img [src]="headerImage" alt="" class="header-image" />
                        }
                        <h1 class="margin-bottom-sm">
                            {{ 'research.collection.' + collectionCode + '.name' | translate }}
                        </h1>
                        @if ('research.collection.' + collectionCode + '.description' | translate) {
                            <p [innerHTML]="'research.collection.' + collectionCode + '.description' | translate"></p>
                        }
                    </mulo-expandable>
                </div>
            </div>
        </mulo-centered-block-layout>
    }
    <ng-template #advSearchRoot="muloSearchbarAdvRoot" muloSearchbarAdvRoot>
        @if (searchHeader && !isCollection) {
            <esp-research-unit-header
                [unitCode]="searchHeader"
                [activeEntity]="activeEntity()"
            ></esp-research-unit-header>
        }

        @if (showSearchbar) {
            <div class="searchbar-section">
                <mulo-searchbar
                    (inputChanged)="handleQueryChange($event, isCollection)"
                    (searchSubmit)="onSearchSubmit($event)"
                    [advSearchLabel]="'research.advanced.search.title' | translate"
                    [advSearchRoot]="advancedSearchEnabled ? advSearchRoot : null"
                    [advSearchStartOpen]="openAdvancedSearchStart"
                    [autocompleteSearchBy]="autocompleteSearchBy"
                    [inProgress]="searchInProgress"
                    [inputValue]="searchQuery"
                    [label]="
                        isCollection
                            ? ('research.search.academic.units.searchForIn'
                              | translate
                                  : {
                                        searchForEntity: (searchbarPlaceholder | translate),
                                        unitName: 'research.collection.' + searchHeader + '.name' | translate,
                                    })
                            : (searchbarPlaceholder | translate)
                    "
                    [clearInputButtonLabel]="'research.aria.search.bar.clear' | translate"
                    size="large"
                >
                    @if (!isCollection) {
                        <mulo-searchbar-options
                            (selectionChange)="handleEntityChange($event)"
                            [label]="'research.aria.search.combobox.entity' | translate"
                            [options]="searchEntities"
                            [value]="activeEntity()"
                            id="discovery-entities"
                            placement="after"
                        ></mulo-searchbar-options>
                    }
                    @if (searchScopesByEntity.length > 0 && !isCollection) {
                        <mulo-searchbar-options
                            (selectionChange)="handleScopeChange($event)"
                            [label]="'research.aria.search.combobox.scope' | translate"
                            [options]="searchScopesByEntity"
                            [value]="activeScope"
                            class="scope-separator"
                            id="discovery-scopes"
                            placement="after"
                        ></mulo-searchbar-options>
                    }
                    @if (!isCollection) {
                        <esp-advanced-search
                            (advancedSearch)="runAdvancedSearch($event)"
                            [initQuery]="searchQueryTemp"
                            [selectedScope]="selectedScope"
                        ></esp-advanced-search>
                    }
                </mulo-searchbar>
            </div>
        }
    </ng-template>
</mulo-prologue>
@if (!showSearchbar && !isCollection) {
    <esp-research-unit-nav
        [activeEntity]="activeEntity()"
        [tabPanel]="tabPanel"
        [unitCode]="searchHeader"
    ></esp-research-unit-nav>
}

@if (activeFilters?.length > 0 && media.is('gt-sm')) {
    <mulo-centered-block-layout @heightInAnimation @heightOutAnimation>
        <div class="active-filters-row">
            <mulo-facets-active
                (remove)="handleFilterRemove($event)"
                (removeAll)="handleFilterRemoveAll()"
                [activeItems]="activeFilters"
                [disabled]="searchInProgress"
                [lockable]="false"
                resetLabel="{{ 'research.facets.reset' | translate }}"
            >
            </mulo-facets-active>
        </div>
    </mulo-centered-block-layout>
}
<mat-tab-nav-panel #tabPanel></mat-tab-nav-panel>

<mulo-centered-block-layout class="main-results" width="full">
    @if (searchInProgress) {
        <mulo-loading-slate>
            <mulo-spinner [diameter]="60"></mulo-spinner>
        </mulo-loading-slate>
    }

    @if (media.is('gt-sm')) {
        <mulo-side-main-layout [sideColumnSize]="showFacets ? '20em' : 0" sideSide="left">
            @if (showFacets) {
                <div class="padding-sm" slot="layout-side">
                    @if (facetsForInterface) {
                        <ng-container *ngTemplateOutlet="facetsBlock"></ng-container>
                    }
                </div>
            }
            <div class="padding-top-sm padding-bottom-sm" slot="layout-main">
                <ng-container *ngTemplateOutlet="searchResultsBlock"></ng-container>
            </div>
        </mulo-side-main-layout>
    }
    @if (media.is('lt-md')) {
        <div class="padding-sm">
            <ng-container *ngTemplateOutlet="searchResultsBlock"></ng-container>
        </div>
    }

    @if (paginationConfig().totalItems) {
        @if (pagesCount() > 1) {
            <div class="pagination-wrap">
                <mulo-floating-toolbar class="margin-top-md">
                    <div slot="toolbar-middle">
                        <pagination-template
                            #p="paginationApi"
                            (pageChange)="pageChange($event)"
                            [id]="paginationConfig().id"
                            [maxSize]="7"
                        >
                            <mulo-paging
                                [data]="p"
                                [directionLinksAsIcons]="media.is('xs')"
                                [hideLastPage]="assets()?.length > 0"
                                [nextLabel]="'research.search.results.next' | translate"
                                [previousLabel]="'research.search.results.previous' | translate"
                                [screenReaderJumpLabel]="'research.aria.results.page.jump.button' | translate"
                                id="pagination"
                            ></mulo-paging>
                        </pagination-template>
                    </div>
                </mulo-floating-toolbar>
            </div>
        }
        <div class="items-count-wrap">
            {{ 'Results per page: ' }}
            <mat-button-toggle-group [value]="paginationConfig().itemsPerPage" (change)="handlePageSizeChange($event)">
                @for (size of [10, 20, 30]; track $index) {
                    <mat-button-toggle [value]="size">{{ size }}</mat-button-toggle>
                }
            </mat-button-toggle-group>
        </div>
    }
</mulo-centered-block-layout>

<ng-template #facetsBlock>
    @if (activeFilters?.length > 0 && media.is('lt-md')) {
        <div class="sidebar-active-filters">
            <div class="sidebar-active-filters-header">
                <h3 class="margin-zero semibold">
                    {{ 'research.facets.active' | translate }}
                </h3>
            </div>
            <mulo-facets-active
                (remove)="handleFilterRemove($event)"
                (removeAll)="handleFilterRemoveAll()"
                [activeItems]="activeFilters"
                [disabled]="searchInProgress"
                [lockable]="false"
                resetLabel="{{ 'research.facets.reset' | translate }}"
            >
            </mulo-facets-active>
        </div>
    }
    <div class="sidebar-active-filters-header">
        <h3 class="padding-left-sm margin-zero h2">
            {{ 'research.results.facets.title' | translate }}
        </h3>
    </div>
    <mulo-facets [disabled]="searchInProgress" [showCheckboxes]="showCheckboxes">
        @if (sortOptions.length > 1 && media.is('gt-sm') && activeEntity() !== 'researchers') {
            <mulo-facet-sort
                (selectedChange)="handleSortChange($event)"
                [options]="sortOptions"
                [selected]="selectedSort"
                label="{{ 'research.results.sortby.title' | translate }}"
            >
            </mulo-facet-sort>
        }
        @for (section of facetsForInterface; track section; let sectionIndex = $index) {
            <mulo-facet-section
                (itemsRevealChange)="handleSectionReveal(section)"
                [expanded]="section.expanded"
                [id]="'facet-section_' + sectionIndex"
                [itemsRevealLabel]="getItemsRevealLabel(section.revealed) | translate"
                [itemsReveal]="section.revealed"
                [selectable]="section.selectable"
                [showRevealButton]="section.items.length > section.limit"
                [showSectionActions]="section.showSectionActions"
                [title]="section.label"
                [a11yHeaderLevel]="4"
            >
                <button (click)="handleApplySelection(section)" color="primary" mat-raised-button slot="section-action">
                    {{ 'research.results.facets.apply' | translate }}
                </button>
                <button
                    (click)="handleClearSelection(section, sectionIndex)"
                    color="primary"
                    mat-button
                    slot="section-action"
                >
                    {{ getClearSelectionLabel(section.key) | translate }}
                </button>
                @for (item of section.items; track item; let itemIndex = $index) {
                    <mulo-facet-item
                        (checkedChange)="handleChecked($event, item, section, sectionIndex)"
                        (clicked)="handleFacetClick($event, item, section)"
                        (excludedChange)="handleExcluded($event, item, section)"
                        [checked]="item.checked"
                        [class.hidden-facet-item]="!section.revealed && itemIndex >= section.limit"
                        [count]="item.count"
                        [excluded]="item.excluded"
                        [id]="'facet-item_' + sectionIndex + '_' + itemIndex"
                        [key]="item.key"
                        [label]="item.label"
                    >
                        @if (
                            section.key === 'facet_creationdate' ||
                            section.key === 'researcher_internal_affiliation_start_date_facet'
                        ) {
                            <mulo-facet-numerical
                                #creationDateFacet
                                (facetChange)="handleDateFacetChange($event, item, section)"
                                (facetSubmit)="handleDateFacetSubmit($event, section)"
                                [fromAriaLabel]="'research.facets.date.from' | translate"
                                [fromLabel]="'research.facets.date.from.short' | translate"
                                [from]="item.from"
                                [labelledBy]="'facet-section_' + sectionIndex + '_title'"
                                [max]="item.to"
                                [min]="item.from"
                                [toAriaLabel]="'research.facets.date.to' | translate"
                                [toLabel]="'research.facets.date.to.short' | translate"
                                [to]="item.to"
                            >
                            </mulo-facet-numerical>
                        }
                    </mulo-facet-item>
                }
            </mulo-facet-section>
        }
    </mulo-facets>
</ng-template>

<ng-template #searchResultsBlock>
    @if (assets()?.length > 0 || researchers?.length > 0 || projects?.length > 0) {
        <div
            (muloStickyScrolled)="toolbarScrolled = $event"
            (muloStickyStuck)="toolbarStuck = $event"
            @heightInAnimation
            @heightOutAnimation
            [class.is-scrolled]="toolbarScrolled"
            [class.is-stuck]="toolbarStuck"
            [muloStickyzIndex]="2"
            class="toolbar"
            muloSticky
            muloStickyOffsetTop="1rem"
        >
            <mulo-floating-toolbar
                [disabled]="searchInProgress"
                [background]="toolbarStuck || toolbarScrolled ? 'opaque' : 'transparent'"
                [shadow]="toolbarStuck || toolbarScrolled"
                class="sticky-numbering-bar"
            >
                <div slot="toolbar-left">
                    @if (assets()?.length > 0) {
                        <mat-checkbox
                            color="primary"
                            class="check-all"
                            (change)="updateCheckedItemsByPage($event.checked)"
                            [checked]="pageItemsAllChecked()"
                            [indeterminate]="pageItemsSomeChecked()"
                        ></mat-checkbox>
                    }
                    <h2 cdkAriaLive="polite" class="toolbar-text">
                        @if (checkedItems().length > 0) {
                            {{ 'You have selected ' + checkedItems().length + ' of ' + totalResults() + ' results' }}
                            <button mat-button color="primary" class="margin-left-sm" (click)="clearCheckedItems()">
                                Clear selection
                            </button>
                        } @else {
                            <ng-container>
                                {{
                                    'research.profiles.search.results.toolbar'
                                        | translate
                                            : {
                                                  start: (currentPageStart() | number),
                                                  end: (currentPageEnd() | number),
                                                  total: (totalResults() | number),
                                              }
                                }}
                            </ng-container>
                            @if (searchQuery && media.is('gt-xs') && (toolbarStuck || toolbarSticky)) {
                                <span
                                    @widthOutAnimation
                                    [@widthInAnimation]="{
                                        value: ':enter',
                                        params: { startScale: '1' },
                                    }"
                                    style="overflow: hidden; white-space: nowrap"
                                >
                                    <span>&nbsp;{{ 'research.profiles.search.toolbar.for' | translate }}&nbsp;</span>
                                    <span class="search-value semibold"
                                        >{{ searchQuery }}
                                        @if (searchScope && searchScope !== 'all') {
                                            <span>
                                                &nbsp;({{
                                                    'research.profiles.search.scope.' + searchScope | translate
                                                }})
                                            </span>
                                        }
                                    </span></span
                                >
                            }
                        }
                    </h2>
                </div>
                <div slot="toolbar-right">
                    <mulo-floating-toolbar-buttons>
                        @if (assets()?.length > 0) {
                            <button
                                mat-button
                                color="primary"
                                [disabled]="checkedItems().length <= 0"
                                (click)="exportAssets(true)"
                            >
                                Export Selected
                            </button>
                        }
                        @if (showViewToggle) {
                            <div
                                @widthOutAnimation
                                [@widthInAnimation]="{
                                    value: ':enter',
                                    params: { startScale: '1' },
                                }"
                            >
                                <mulo-toggle-button
                                    (stateChange)="changeToggle($event)"
                                    *mediaIf="'gt-sm'"
                                    [state]="toggleButtonState"
                                    aria-label="{{ getAriaLabelForToggle() | translate }}"
                                >
                                    <mat-icon slot="left" svgIcon="view-tiles"></mat-icon>
                                    <mat-icon
                                        slot="right"
                                        class="mat-icon-rtl-mirror"
                                        svgIcon="view-list-icons"
                                    ></mat-icon>
                                </mulo-toggle-button>
                            </div>
                        }
                        @if (toolbarStuck || media.is('lt-md')) {
                            <mulo-floating-toolbar-separator style="height: 1rem"> </mulo-floating-toolbar-separator>
                        }
                        @if (toolbarStuck) {
                            <button
                                (click)="scrollToTop()"
                                @widthOutAnimation
                                [@widthInAnimation]="{
                                    value: ':enter',
                                    params: { startScale: '1' },
                                }"
                                [attr.aria-label]="'research.portal.fulldisplay.backToTopButton' | translate"
                                class="margin-zero"
                                mat-icon-button
                            >
                                <mat-icon svgIcon="arrow-up"></mat-icon>
                            </button>
                        }
                        @if (media.is('lt-md')) {
                            <button
                                (click)="toggleFacetsSidenav()"
                                @widthOutAnimation
                                [@widthInAnimation]="{
                                    value: ':enter',
                                    params: { startScale: '1' },
                                }"
                                [attr.aria-label]="'research.results.facets.title' | translate"
                                class="margin-zero"
                                mat-icon-button
                            >
                                <mat-icon svgIcon="filter"></mat-icon>
                            </button>
                            @if (sortOptions.length > 1 && activeEntity() !== 'researchers') {
                                <button
                                    @widthOutAnimation
                                    [@widthInAnimation]="{
                                        value: ':enter',
                                        params: { startScale: '1' },
                                    }"
                                    [attr.aria-label]="'research.results.sortby.title' | translate"
                                    [matMenuTriggerFor]="sortMenu"
                                    class="margin-zero"
                                    mat-icon-button
                                >
                                    <mat-icon svgIcon="filter-variant"></mat-icon>
                                </button>
                            }
                            <mat-menu
                                #sortMenu="matMenu"
                                [aria-label]="'research.results.sortby.title' | translate"
                                class="mat-primary"
                            >
                                <h3 class="sort-menu-title">
                                    {{ 'research.results.sortby.title' | translate }}
                                </h3>
                                @for (option of sortOptions; track option) {
                                    <button
                                        (click)="handleSortChange(option.id)"
                                        [class.mat-selected]="selectedSort === option.id"
                                        class="mat-option sort-option"
                                        mat-menu-item
                                    >
                                        {{ option.value }}
                                    </button>
                                }
                            </mat-menu>
                        }
                    </mulo-floating-toolbar-buttons>
                </div>
            </mulo-floating-toolbar>
            @if (showUnitSearchbar && searchHeader && !toolbarStuck && !isCollection) {
                <div
                    @widthOutAnimation
                    [@widthInAnimation]="
                        searchQueryTemp
                            ? { value: '', params: { time: 0 } }
                            : {
                                  value: ':enter',
                                  params: { startScale: '1' },
                              }
                    "
                    class="small-search-wrap"
                >
                    <mulo-separator [length]="24" class="margin-left-xs margin-right-xs"></mulo-separator>
                    <mulo-searchbar
                        (inputChanged)="handleQueryChange($event, true)"
                        (searchSubmit)="onSearchSubmit($event)"
                        [autocompleteSearchBy]="autocompleteSearchBy"
                        [inProgress]="searchInProgress"
                        [inputValue]="searchQueryTemp"
                        [label]="
                            'research.search.academic.units.searchForIn'
                                | translate
                                    : {
                                          searchForEntity: (searchbarPlaceholder | translate),
                                          unitName: (searchHeader | translate),
                                      }
                        "
                        class="small-search"
                        color="none"
                        [size]="searchQueryTemp ? 'small' : 'mini'"
                        [clearInputButtonLabel]="'research.aria.search.bar.clear' | translate"
                    ></mulo-searchbar>
                </div>
            }
        </div>

        <div [class.is-loading]="true" class="search-results-wrapper">
            @if (assets().length > 0) {
                @for (asset of assets() | paginate: paginationConfig(); track asset.id) {
                    <div class="search-result-item">
                        <span class="item-count">{{ currentPageStart() + $index }}</span>
                        <mat-checkbox
                            color="primary"
                            class="check-all"
                            (change)="updateCheckedItems($event.checked, asset)"
                            [checked]="isAssetChecked(asset)"
                        ></mat-checkbox>

                        <exl-citation-brief
                            [altmetric]="displayAltmetric"
                            [authors]="getAuthors(asset.creators)"
                            [disabled]="searchInProgress"
                            [doi]="asset.doi"
                            [lines]="getLines(asset)"
                            [postType]="
                                asset.resourceSubType
                                    ? ' - ' +
                                      (asset.resourceSubType | defaultTranslate: { default: asset.resourceSubType })
                                    : ''
                            "
                            [queryParams]="getFullAssetPageQueryParams()"
                            [router]="asset.permalink"
                            [thumbs]="[{ alt: '', url: getThumbnail(asset) }]"
                            [titleClickable]="true"
                            [title]="asset.title"
                            [titleHeaderLevel]="3"
                            [type]="'portal.' + asset.resourceType"
                            class="is-hoverable"
                            thumbsWidth="70px"
                            [class.is-checked]="isAssetChecked(asset)"
                        >
                            <ng-container slot="additional-status-tags">
                                @if (asset.openaccess) {
                                    <mulo-separator [length]="14" orientation="vertical"></mulo-separator>
                                    <mulo-status-tag
                                        class="open-acces"
                                        flavor="plain"
                                        [icon]="useAltOAIcon() ? 'reading' : 'open-access'"
                                    >
                                        {{ 'research.results.openaccess' | translate }}
                                    </mulo-status-tag>
                                }
                                @if (asset.peerreview) {
                                    <mulo-separator
                                        [length]="14"
                                        class="margin-right-xs"
                                        orientation="vertical"
                                    ></mulo-separator>
                                    <mulo-status-tag class="peer-reviewed" flavor="plain" icon="peer-reviewed">
                                        {{ 'research.results.peerreviewed' | translate }}
                                    </mulo-status-tag>
                                }
                            </ng-container>
                            @if (asset.description) {
                                <mulo-expandable
                                    [descriptionLabel]="asset.title"
                                    [ratio]="3"
                                    buttonPosition="right"
                                    class="abstract"
                                    collapseLabel="{{ 'research.results.description.collapse' | translate }}"
                                    expandLabel="...{{ 'research.results.description.expand' | translate }}"
                                >
                                    <p [innerHTML]="asset.description | htmlEscape: false"></p>
                                </mulo-expandable>
                            }
                        </exl-citation-brief>
                    </div>
                }
            }
            @if (projects?.length > 0) {
                @for (project of projects | paginate: paginationConfig(); track project) {
                    <exl-citation-brief
                        [authors]="project.memberResearchers.concat(project.memberOrganizations)"
                        [queryParams]="{ institution: institution }"
                        [router]="project.projectUrl"
                        [titleClickable]="true"
                        [title]="project.name | translateObject"
                        [titleHeaderLevel]="3"
                        [type]="project.typeCode"
                    >
                        @if (organizationsCodes(project); as organizations) {
                            <p class="semibold">
                                @for (organization of organizations; track organization; let isLast = $last) {
                                    <span> {{ organization | translate }}{{ isLast ? '' : ', ' }} </span>
                                }
                            </p>
                        }
                        @if (project.startDate) {
                            <span class="additional-data">
                                {{ project.startDate | espdateformat }}
                                @if (project.extras?.startDateLabel === 'mng.projects.expected.start_date') {
                                    <span class="margin-left-xs">{{
                                        'research.projects.list.brief.project.date.expected' | translate
                                    }}</span>
                                }
                                <span>&ndash;</span>
                            </span>
                        }
                        @if (project.endDate) {
                            <span class="additional-data">
                                <span>{{ project.endDate | espdateformat }}</span>
                                @if (project.extras?.endDateLabel === 'mng.projects.expected.end_date') {
                                    <span class="margin-left-xs">
                                        {{ 'research.projects.list.brief.project.date.expected' | translate }}
                                    </span>
                                }
                            </span>
                        }
                        @if (project.description | translateObject; as description) {
                            <mulo-expandable
                                [ratio]="3"
                                buttonPosition="right"
                                collapseLabel="{{ 'research.results.description.collapse' | translate }}"
                                expandLabel="...{{ 'research.results.description.expand' | translate }}"
                            >
                                <p [innerHTML]="description | htmlSanitize"></p>
                            </mulo-expandable>
                        }
                    </exl-citation-brief>
                }
            }
            @if (researchers && researchers.length > 0) {
                <esp-discovery-results
                    [maxWidth]="780"
                    [resultsCount]="totalResults()"
                    [view]="viewAsTiles ? 'tiles' : 'list'"
                >
                    @for (researcher of researchers | paginate: paginationConfig(); track researcher) {
                        <exl-person-brief
                            (clicked)="onPersonClick(researcher.urlIdentifier)"
                            [style.max-width]="!viewAsTiles ? '780px' : null"
                        >
                            <mulo-avatar
                                [class.disable-float]="viewAsTiles && showFacets"
                                [description]="
                                    'research.alt.profile.img.with.name' | translate: { value: researcher.displayName }
                                "
                                [image]="researcher.imageURL"
                                [position]="{ x: 0, y: 20 }"
                                [radius]="4"
                                [size]="!viewAsTiles ? '80' : '120'"
                            >
                            </mulo-avatar>
                            <span [muloElementAsHeading]="4">
                                @if (researcher.displayName) {
                                    <a
                                        [innerHTML]="
                                            researcher.displayName
                                                | highlight
                                                    : (resultsByName ? searchQueryWithoutLastAsterisk(searchQuery) : '')
                                        "
                                        [queryParams]="{ institution: institution }"
                                        [routerLink]="['/profile/' + researcher.urlIdentifier]"
                                        [titleAsLink]="false"
                                        attr.aria-label="{{
                                            'research.aria.search.go.to.profile'
                                                | translate: { value: researcher.displayName }
                                        }}"
                                        exlPersonBriefTitle
                                    >
                                    </a>
                                }
                            </span>
                            <p>
                                @if (researcher.displayTitle) {
                                    {{ researcher.displayTitle }}
                                } @else {
                                    @if (researcher?.currentAffiliation?.length > 0) {
                                        @for (aff of researcher.currentAffiliation; track aff; let isLast = $last) {
                                            {{
                                                aff
                                                    | researcherTitle
                                                        : {
                                                              generalPosition: researcher.generalPosition,
                                                              suffix: ',',
                                                          }
                                            }}
                                            <span
                                                [innerHTML]="
                                                    aff.organizationCode
                                                        | translate
                                                        | highlight
                                                            : (resultsByAffiliation
                                                                  ? searchQueryWithoutLastAsterisk(searchQuery)
                                                                  : '')
                                                "
                                            >
                                            </span>
                                            @if (!isLast) {
                                                ;
                                            }
                                        }
                                    }
                                }
                            </p>
                            @if (researcher.topics && researcher.topics.length > 0) {
                                <p class="brief-tags" slot="brief-tags">
                                    <mat-icon muloSvgViewbox svgIcon="tag-multiple"></mat-icon>
                                    <exl-tags>
                                        @for (
                                            tag of researcher.topics | slice: 0 : maxVisibleTags;
                                            track tag;
                                            let isLast = $last
                                        ) {
                                            <exl-tag [label]="tag"></exl-tag>
                                            @if (!isLast) {
                                                <mulo-separator></mulo-separator>
                                            }
                                        }
                                        @if (researcher.topics.length > maxVisibleTags) {
                                            <span class="italics">
                                                ... ({{
                                                    'research.profiles.search.results.number.of.interests'
                                                        | translate
                                                            : {
                                                                  val0: researcher.topics.length,
                                                              }
                                                }})
                                            </span>
                                        }
                                    </exl-tags>
                                </p>
                            }
                        </exl-person-brief>
                    }
                </esp-discovery-results>
            }
        </div>
    } @else {
        @if (state && searchHeader && !isCollection) {
            <div class="small-search-wrap unit-error-search-wrap">
                <mulo-searchbar
                    (inputChanged)="handleQueryChange($event)"
                    (searchSubmit)="onSearchSubmit($event)"
                    [autocompleteSearchBy]="autocompleteSearchBy"
                    [inProgress]="searchInProgress"
                    [inputValue]="searchQuery"
                    [label]="
                        'research.search.academic.units.searchForIn'
                            | translate
                                : {
                                      searchForEntity: (searchbarPlaceholder | translate),
                                      unitName: (searchHeader | translate),
                                  }
                    "
                    [clearInputButtonLabel]="'research.aria.search.bar.clear' | translate"
                    class="small-search"
                    color="none"
                    size="small"
                ></mulo-searchbar>
            </div>
        }
        @if (state) {
            <mulo-centered-block-layout
                [@opacityInAnimation]="{ value: 'enter', params: { time: '1s' } }"
                [width]="590"
                class="state-msg-wrap"
            >
                <div style="text-align: center">
                    <p class="state-msg">
                        <span [innerHTML]="stateMessage | htmlSanitize" cdkAriaLive="polite"></span>
                    </p>
                    @if (state === 'ASSETS_SEARCH') {
                        <div class="state-msg-try-again">
                            {{ 'research.search.results.assets.noResults.tryAgain' | translate }}
                        </div>
                    }
                </div>
            </mulo-centered-block-layout>
        }
    }
</ng-template>
